import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import SuperAdminAccountsAPI from '../../api/superAdmin/accounts';
import SuperAdminAccountUsersAPI from '../../api/superAdmin/accountUsers';

export const state = {
  records: [],
  meta: { totalCount: 0 },
  accountUsers: {}, // account_id => [account_user, ...], populated on demand (fetchAccount/fetchAccountUsers)
  uiFlags: {
    fetchingList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    fetchingUsers: false,
    addingUser: false,
    updatingUser: false,
  },
};

export const getters = {
  getSuperAdminAccounts($state) {
    return $state.records;
  },
  getSuperAdminAccountsMeta($state) {
    return $state.meta;
  },
  getSuperAdminAccountUsers: $state => accountId => {
    return $state.accountUsers[accountId] || [];
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  fetchAccounts: async function fetchAccounts({ commit }, params = {}) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      fetchingList: true,
    });
    try {
      const { data } = await SuperAdminAccountsAPI.get(params);
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS, data.accounts);
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_META, {
        totalCount: data.total_count,
      });
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        fetchingList: false,
      });
    }
  },

  fetchAccount: async function fetchAccount({ commit }, id) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      fetchingItem: true,
    });
    try {
      const { data } = await SuperAdminAccountsAPI.show(id);
      commit(types.default.EDIT_SUPER_ADMIN_ACCOUNT, data);
      commit(types.default.SET_SUPER_ADMIN_ACCOUNT_USERS, {
        accountId: id,
        accountUsers: data.account_users,
      });
      return data;
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        fetchingItem: false,
      });
    }
  },

  createAccount: async function createAccount({ commit }, payload) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      creatingItem: true,
    });
    try {
      const { data } = await SuperAdminAccountsAPI.create({ account: payload });
      commit(types.default.ADD_SUPER_ADMIN_ACCOUNT, data.account);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        creatingItem: false,
      });
    }
  },

  updateAccountStatus: async function updateAccountStatus(
    { commit },
    { id, status }
  ) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      updatingItem: true,
    });
    try {
      const { data } = await SuperAdminAccountsAPI.update(id, {
        account: { status },
      });
      commit(types.default.EDIT_SUPER_ADMIN_ACCOUNT, data);
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        updatingItem: false,
      });
    }
  },

  fetchAccountUsers: async function fetchAccountUsers({ commit }, accountId) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      fetchingUsers: true,
    });
    try {
      const { data } = await SuperAdminAccountUsersAPI.get(accountId);
      commit(types.default.SET_SUPER_ADMIN_ACCOUNT_USERS, {
        accountId,
        accountUsers: data,
      });
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        fetchingUsers: false,
      });
    }
  },

  addAccountUser: async function addAccountUser(
    { commit },
    { accountId, ...payload }
  ) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      addingUser: true,
    });
    try {
      const { data } = await SuperAdminAccountUsersAPI.create(accountId, {
        account_user: payload,
      });
      commit(types.default.ADD_SUPER_ADMIN_ACCOUNT_USER, { accountId, data });
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        addingUser: false,
      });
    }
  },

  updateAccountUser: async function updateAccountUser(
    { commit },
    { accountId, id, ...payload }
  ) {
    commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
      updatingUser: true,
    });
    try {
      const { data } = await SuperAdminAccountUsersAPI.update(accountId, id, {
        account_user: payload,
      });
      commit(types.default.EDIT_SUPER_ADMIN_ACCOUNT_USER, { accountId, data });
      return data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG, {
        updatingUser: false,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_SUPER_ADMIN_ACCOUNTS_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },

  [types.default.SET_SUPER_ADMIN_ACCOUNTS]: MutationHelpers.set,
  [types.default.ADD_SUPER_ADMIN_ACCOUNT]: MutationHelpers.create,
  [types.default.EDIT_SUPER_ADMIN_ACCOUNT]: MutationHelpers.update,

  [types.default.SET_SUPER_ADMIN_ACCOUNTS_META](_state, meta) {
    _state.meta = meta;
  },

  [types.default.SET_SUPER_ADMIN_ACCOUNT_USERS](
    _state,
    { accountId, accountUsers }
  ) {
    _state.accountUsers = { ..._state.accountUsers, [accountId]: accountUsers };
  },

  [types.default.ADD_SUPER_ADMIN_ACCOUNT_USER](_state, { accountId, data }) {
    const existing = _state.accountUsers[accountId] || [];
    _state.accountUsers = {
      ..._state.accountUsers,
      [accountId]: [...existing, data],
    };
  },

  [types.default.EDIT_SUPER_ADMIN_ACCOUNT_USER](_state, { accountId, data }) {
    const existing = _state.accountUsers[accountId] || [];
    _state.accountUsers = {
      ..._state.accountUsers,
      [accountId]: existing.map(au => (au.id === data.id ? data : au)),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
